// Relive-style photo moments drawn over the flyover video (lib/flyover_renderer/entry.ts): when the
// flight stops where photos were taken, the first grows out of a pin there to fill the frame over a
// blurred copy of itself, the stop's photos play as a swipe slideshow (with page dots), and the last shrinks
// back into the pin — which stays on the route behind the walker, badged with how many photos it holds.
// Pins only appear once reached: upcoming ones would crowd the summit pins and give the walk away.
//
// DOM over the map canvas, positioned once per captured frame after the map has settled, on the clip
// clock (never wall time), so every frame is reproducible. The timing and geometry live in
// flyoverPhotos.ts, where they are unit-tested.

import {
  FlyoverPhotoStopConfig, PlacedStop, PhotoStops, Bounds, Rect,
  placeStops, photoOpen, photoZoom, slideAt, stopMs, fitPhotoRect, lerpRect, swipeLayout, activeDot
} from "./flyoverPhotos";

/** How long the render waits for one photo before leaving it out. */
const LOAD_TIMEOUT_MS = 30000;
const FONT = '-apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export interface PhotoOverlay {
  /** The stops (0..1, ascending) the flight lands on — only stops with a photo that actually loaded. */
  fracs(): number[];
  /** How long each of those stops holds the flight (ms), in the same order. */
  durations(): number[];
  /** Photos sent / photos that loaded and will be shown. */
  counts(): { sent: number; shown: number };
  /**
   * Loads every photo. The video awaits it and drops what fails, so no captured frame is blank; the
   * live flyover starts it without waiting (`dropFailed: false`), keeping every stop where the flight
   * was told it is.
   */
  preload(opts?: { dropFailed?: boolean }): Promise<void>;
  /** Position and animate everything for this frame. */
  update(stops: StopState): void;
  destroy(): void;
}

/** What update needs to know about the stops: the one up now, and which have been reached. */
export type StopState = Pick<PhotoStops, "active" | "wasShown">;

interface LoadedPhoto { src: string; w: number; h: number }

interface StopEls {
  placed: PlacedStop;
  photos: LoadedPhoto[];
  pin: HTMLElement;
}

/** One photo on the stage: its blurred backdrop and its own framed card, which swipe as a pair. */
interface Layer { backdrop: HTMLElement; card: HTMLElement; img: HTMLImageElement; src: string }

export function createPhotoOverlay(
  map: any,
  stopsConfig: FlyoverPhotoStopConfig[] | null | undefined,
  coords: number[][],
  cumKm: number[],
  /** Where the full photo may sit: clear of the logo and the platform's caption area. */
  safeBounds: () => Bounds,
  options: {
    zIndex?: number;
    stageZIndex?: number;
    /** What the overlay is laid over; positions are in its coordinates. Defaults to the page (video). */
    container?: HTMLElement;
    /** Size multiplier against a 1080px-wide frame. Defaults to the frame's own (video). */
    scale?: number;
  } = {}
): PhotoOverlay | null {
  const placed = placeStops(stopsConfig, coords, cumKm);
  const sent = Array.isArray(stopsConfig)
    ? stopsConfig.reduce((n, s) => n + (Array.isArray(s?.photos) ? s.photos.length : 0), 0)
    : 0;
  if (!placed.length) return null;

  const container = options.container || document.body;
  const size = () => (container === document.body
    ? { w: window.innerWidth, h: window.innerHeight }
    : { w: container.clientWidth, h: container.clientHeight });
  let { w: vw, h: vh } = size();
  const u = options.scale ?? Math.min(vw, vh) / 1080;
  const pinPx = Math.round(80 * u);
  const border = Math.max(2, Math.round(5 * u));

  // Pins sit with the summit pins, under the stats panel; the stage covers everything but the logo.
  const pinLayer = document.createElement("div");
  pinLayer.style.cssText = `position:absolute;inset:0;z-index:${options.zIndex ?? 9};pointer-events:none;overflow:hidden`;
  const stage = document.createElement("div");
  stage.style.cssText = `position:absolute;inset:0;z-index:${options.stageZIndex ?? 25};pointer-events:none;overflow:hidden;display:none`;
  const layer = (): Layer => {
    const backdrop = document.createElement("div");
    backdrop.style.cssText = "position:absolute;inset:-60px;background-size:cover;background-position:center;filter:blur(36px) brightness(.55)";
    const card = document.createElement("div");
    card.style.cssText = `position:absolute;left:0;top:0;overflow:hidden;border:${border}px solid #fff;box-sizing:border-box;` +
      `box-shadow:0 ${Math.round(14 * u)}px ${Math.round(44 * u)}px rgba(0,0,0,.6);background:#111;transform-origin:50% 50%`;
    const img = document.createElement("img");
    img.style.cssText = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;transform-origin:50% 50%";
    card.appendChild(img);
    return { backdrop, card, img, src: "" };
  };
  // Two of each: the photo that's up, and the one swiping in after it.
  const layers = [layer(), layer()];
  // Page dots under the card, for a stop with more than one photo.
  const dots = document.createElement("div");
  dots.style.cssText = `position:absolute;left:0;top:0;display:flex;gap:${Math.round(8 * u)}px;transform:translateX(-50%)`;
  stage.append(layers[0].backdrop, layers[1].backdrop, layers[0].card, layers[1].card, dots);
  container.append(pinLayer, stage);
  const dotStyle = (on: boolean) =>
    `height:${Math.round(6 * u * 1.6)}px;width:${Math.round((on ? 18 : 6) * u * 1.6)}px;border-radius:${Math.round(3 * u * 1.6)}px;` +
    `background:#fff;opacity:${on ? 1 : 0.5};box-shadow:0 1px ${Math.round(3 * u)}px rgba(0,0,0,.5)`;
  const drawDots = (count: number, active: number) => {
    if (count < 2) { dots.style.display = "none"; return; }
    dots.style.display = "flex";
    if (dots.childElementCount !== count) dots.innerHTML = "<div></div>".repeat(count);
    Array.from(dots.children).forEach((d, i) => { (d as HTMLElement).style.cssText = dotStyle(i === active); });
  };
  const placeCard = (l: Layer, r: Rect, radius: number, dx = 0, scale = 1) => {
    l.card.style.width = `${r.w}px`; l.card.style.height = `${r.h}px`;
    l.card.style.borderRadius = `${radius}px`;
    l.card.style.transform = `translate(${r.x + dx}px,${r.y}px) scale(${scale})`;
  };

  const show = (l: Layer, src: string) => {
    if (l.src === src) return;
    l.src = src;
    l.img.src = src;
    l.backdrop.style.backgroundImage = `url("${src.replace(/"/g, "%22")}")`;
  };

  let els: StopEls[] = placed.map((p) => {
    const pin = document.createElement("div");
    pin.style.cssText = `position:absolute;left:0;top:0;width:${pinPx}px;height:${pinPx}px;display:none`;
    pinLayer.appendChild(pin);
    // Sizes unknown until loaded; fitPhotoRect treats 0x0 as 4:3 meanwhile.
    const e = { placed: p, photos: p.stop.photos.map((ph) => ({ src: ph.url, w: 0, h: 0 })), pin };
    buildPin(e);
    return e;
  });

  // The pin floats just above its spot on the line, with the line's spot directly under its centre.
  const pinRect = (pt: { x: number; y: number }): Rect =>
    ({ x: pt.x - pinPx / 2, y: pt.y - pinPx - 8 * u, w: pinPx, h: pinPx });

  const load = (url: string) => new Promise<LoadedPhoto | null>((resolve) => {
    const img = new Image();
    img.decoding = "sync";
    const timer = setTimeout(() => resolve(null), LOAD_TIMEOUT_MS);
    img.onload = () => {
      clearTimeout(timer);
      // decode() so the first frame that shows it is never caught mid-decode.
      img.decode().catch(() => undefined).then(() =>
        resolve(img.naturalWidth > 0 && img.naturalHeight > 0 ? { src: img.src, w: img.naturalWidth, h: img.naturalHeight } : null));
    };
    img.onerror = () => { clearTimeout(timer); resolve(null); };
    img.src = url;
  });

  function buildPin(e: StopEls) {
    const count = e.photos.length;
    const badge = count > 1
      ? `<div style="position:absolute;right:${-6 * u}px;top:${-6 * u}px;min-width:${30 * u}px;height:${30 * u}px;padding:0 ${7 * u}px;` +
        `box-sizing:border-box;border-radius:${15 * u}px;background:#FFD400;color:#111;font:800 ${18 * u}px/${30 * u}px ${FONT};` +
        `text-align:center;box-shadow:0 1px ${4 * u}px rgba(0,0,0,.5)">${count}</div>`
      : "";
    e.pin.innerHTML =
      `<div style="width:100%;height:100%;border-radius:50%;overflow:hidden;border:${border}px solid #fff;box-sizing:border-box;` +
      `box-shadow:0 ${4 * u}px ${14 * u}px rgba(0,0,0,.55);background:#111">` +
      `<img src="${e.photos[0].src.replace(/"/g, "&quot;")}" style="width:100%;height:100%;object-fit:cover;display:block"/></div>${badge}`;
  }

  return {
    fracs: () => els.map((e) => e.placed.frac),
    durations: () => els.map((e) => stopMs(e.photos.length)),
    counts: () => ({ sent, shown: els.reduce((n, e) => n + e.photos.length, 0) }),
    async preload({ dropFailed = true } = {}) {
      const loaded = await Promise.all(els.map((e) => Promise.all(e.placed.stop.photos.map((p) => load(p.url)))));
      const kept: StopEls[] = [];
      els.forEach((e, i) => {
        e.photos = loaded[i].filter((p): p is LoadedPhoto => p !== null);
        if (e.photos.length) { buildPin(e); kept.push(e); return; }
        // Live, a stop the flight already knows about stays (empty) so the indices still line up.
        if (dropFailed) e.pin.remove(); else kept.push(e);
      });
      els = kept;
    },
    update(stops: StopState) {
      const now = size();
      if (now.w !== vw || now.h !== vh) { vw = now.w; vh = now.h; }
      const active = stops.active();
      els.forEach((e, i) => {
        const pt = map.project(e.placed.at);
        const onScreen = Number.isFinite(pt.x) && Number.isFinite(pt.y) &&
          pt.x > -pinPx && pt.x < vw + pinPx && pt.y > -pinPx && pt.y < vh + pinPx * 2;
        // Not yet reached, or its slideshow is up (then the pin IS the frame).
        const hidden = !onScreen || !e.photos.length || !stops.wasShown(i) ||
          (active?.index === i && photoOpen(active.elapsedMs, e.photos.length) > 0);
        e.pin.style.display = hidden ? "none" : "block";
        if (!hidden) {
          const r = pinRect(pt);
          e.pin.style.transform = `translate(${r.x}px,${r.y}px)`;
        }
      });

      const e = active ? els[active.index] : null;
      const open = e && e.photos.length ? photoOpen(active!.elapsedMs, e.photos.length) : 0;
      if (!e || open <= 0) { stage.style.display = "none"; return; }

      const slide = slideAt(active!.elapsedMs, e.photos.length);
      const cur = e.photos[slide.index];
      const nxt = slide.next >= 0 ? e.photos[slide.next] : null;
      const mix = nxt ? slide.mix : 0;
      show(layers[0], cur.src);
      if (nxt) show(layers[1], nxt.src);

      const bounds = safeBounds();
      // A circle in the pin, a softly rounded photo at full size.
      const rounded = 22 * u;
      const full = fitPhotoRect(cur.w, cur.h, bounds);
      const r = lerpRect(pinRect(map.project(e.placed.at)), full, open);
      const round = Math.min(r.w, r.h) / 2;
      stage.style.display = "block";
      // The incoming backdrop fades in OVER a still-solid outgoing one: two half-faded layers would let
      // the map show through mid-swipe.
      layers[0].backdrop.style.opacity = String(open);
      layers[1].backdrop.style.opacity = String(open * mix);
      layers[0].img.style.transform = `scale(${photoZoom(slide.slideMs)})`;
      layers[1].img.style.transform = "scale(1)";

      if (nxt) {
        // Mid-swipe: each photo in its own card, the outgoing one sliding off left as the next comes in
        // from the right, both dipping a little for depth.
        const swipe = swipeLayout(mix, bounds.right - bounds.left + 24 * u);
        placeCard(layers[0], full, rounded, swipe.outDx, swipe.scale);
        placeCard(layers[1], fitPhotoRect(nxt.w, nxt.h, bounds), rounded, swipe.inDx, swipe.scale);
        layers[1].card.style.display = "block";
      } else {
        placeCard(layers[0], r, round + (rounded - round) * open);
        layers[1].card.style.display = "none";
      }

      drawDots(e.photos.length, activeDot(slide));
      dots.style.opacity = String(open);
      dots.style.left = `${(bounds.left + bounds.right) / 2}px`;
      dots.style.top = `${Math.min(full.y + full.h + 14 * u * 1.6, bounds.bottom + 14 * u)}px`;
    },
    destroy() { pinLayer.remove(); stage.remove(); }
  };
}
